import { Inject, inject, Injectable } from '@angular/core'
import { RafflesFacade, RaffleStatusEnum } from '@galabid/gb-dashboard/raffle/data-access'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { catchError, EMPTY, exhaustMap, filter, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { RafflesApiService } from './raffles-api.service'
import * as RafflesActions from './raffles.actions'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { DialogData } from 'libs/shared/ui/src/lib/gb-confirm-dialog/gb-confirm-dialog-data'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbRaffleDrawStore } from './gb-raffle-draw.store'
import { gbCheckLoaded } from 'libs/shared/utils/operators/src/lib/gb-check-loaded/gb-check-loaded.operator'
import { GbConcludeRaffleDialogComponent } from 'libs/gb-dashboard/raffle/feature/src/lib/gb-conclude-raffle-dialog/gb-conclude-raffle-dialog.component'

@Injectable()
export class RafflesEffects {
  private actions$ = inject(Actions)

  loadRaffles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.loadRaffles),
      gbCheckLoaded(this.selectedAuctionFacade.urlName$),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.rafflesApiService
          .getRaffleItems(urlName, action.limit || this.appConfig.itemsPageSize, action.offset || 0, action.ordering)
          .pipe(
            map((response) => RafflesActions.loadRafflesSuccess({ items: response.results, count: response.count })),
            catchError((error) => of(RafflesActions.loadRafflesFailure({ error }))),
          ),
      ),
    ),
  )

  syncRaffleItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.syncRaffleItem),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.rafflesApiService.getRaffleItem(urlName, action.uuid).pipe(
          map((response) => RafflesActions.syncRaffleItemSuccess({ update: { id: response.uuid, changes: response } })),
          catchError((error) => of(RafflesActions.syncRaffleItemFailure({ error }))),
        ),
      ),
    ),
  )

  openConfirmUpdateRaffleStatusDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.openConfirmUpdateRaffleStatusDialog),
      exhaustMap((action) => {
        const dialogData = this.getRaffleStatusDialogData(action.raffleStatus)
        if (!dialogData) return EMPTY
        return this.gbDashboardDialogService.filterByConfirmDialog(dialogData, action)
      }),
      map((action) => RafflesActions.updateRaffleItem({ raffle_status: action.raffleStatus })),
    ),
  )

  updateRaffleItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.updateRaffleItem),
      tap((action) => this.gbLoaderService.showLoaderDialog(action.type)),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.rafflesFacade.selectedItem$),
      switchMap(([action, urlName, selectedItem]) =>
        this.rafflesApiService.updateRaffleItem(urlName, selectedItem!.raffle_item!.uuid, action.changes).pipe(
          tap(() => action.onSuccess?.()),
          map((raffleItem) =>
            RafflesActions.updateRaffleItemSuccess({
              update: { id: selectedItem!.uuid, changes: { raffle_item: raffleItem } },
            }),
          ),
          catchError((error) => of(RafflesActions.updateRaffleItemFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(action.type)),
        ),
      ),
    ),
  )

  openConfirmConcludeRaffleDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.openConfirmConcludeRaffleDialog),
      exhaustMap(() =>
        this.gbDashboardDialogService.getDialogObs(GbConcludeRaffleDialogComponent, {
          maxWidth: '400px',
        }),
      ),
      filter((result) => typeof result === 'boolean'),
      map((result) => RafflesActions.concludeRaffle({ sendNotifications: result })),
    ),
  )

  concludeRaffle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RafflesActions.concludeRaffle),
      tap((action) => this.gbLoaderService.showLoaderDialog(action.type)),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.rafflesFacade.selectedItem$),
      switchMap(([action, urlName, selectedItem]) => {
        return this.rafflesApiService
          .concludeRaffle(urlName, selectedItem!.raffle_item!.uuid, action.sendNotifications)
          .pipe(
            map(() =>
              RafflesActions.concludeRaffleSuccess({
                update: {
                  id: selectedItem!.uuid,
                  changes: {
                    raffle_item: { ...selectedItem!.raffle_item!, raffle_status: RaffleStatusEnum.Closed },
                  },
                },
              }),
            ),
            catchError((error) => of(RafflesActions.concludeRaffleFailure({ error }))),
            finalize(() => this.gbLoaderService.hideLoaderDialog(action.type)),
          )
      }),
    ),
  )

  createRaffleDrawForRealtime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RafflesActions.createRaffleDrawForRealtime),
        tap((action) => this.gbRaffleDrawStore.upsert(action.data)),
      ),
    { dispatch: false },
  )

  deleteRaffleDrawForRealtime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RafflesActions.deleteRaffleDrawForRealtime),
        tap((action) => this.gbRaffleDrawStore.remove(action.uuid)),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RafflesActions.loadRafflesFailure,
          RafflesActions.concludeRaffleFailure,
          RafflesActions.updateRaffleItemFailure,
        ),
        tap((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private rafflesFacade: RafflesFacade,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private rafflesApiService: RafflesApiService,
    private gbLoaderService: GbLoaderService,
    private gbRaffleDrawStore: GbRaffleDrawStore,
    @Inject(APP_CONFIG) protected appConfig: EnvironmentModel,
  ) {}

  private getRaffleStatusDialogData(raffleStatus: RaffleStatusEnum): DialogData | undefined {
    const dialogMap: Partial<Record<RaffleStatusEnum, DialogData>> = {
      [RaffleStatusEnum.Open]: {
        title: 'RAFFLE_CONTROLS.CONFIRM.OPEN_TITLE',
        body: 'RAFFLE_CONTROLS.CONFIRM.OPEN_DESC',
        confirmButtonText: 'RAFFLE_CONTROLS.CONFIRM.OPEN_OK_TEXT',
        cancelButtonText: 'RAFFLE_CONTROLS.CONFIRM.OPEN_CANCEL_TEXT',
      },
      [RaffleStatusEnum.Drawing]: {
        title: 'RAFFLE_CONTROLS.CONFIRM.BEGIN_TITLE',
        body: 'RAFFLE_CONTROLS.CONFIRM.BEGIN_DESC',
        confirmButtonText: 'RAFFLE_CONTROLS.CONFIRM.OK_TEXT',
        cancelButtonText: 'RAFFLE_CONTROLS.CONFIRM.CANCEL_TEXT',
      },
      [RaffleStatusEnum.Closed]: {
        title: 'RAFFLE_CONTROLS.CONFIRM.CONCLUDE_TITLE',
        body: 'RAFFLE_CONTROLS.CONFIRM.CONCLUDE_DESC',
        confirmButtonText: 'RAFFLE_CONTROLS.CONFIRM.CONCLUDE_OK_TEXT',
        cancelButtonText: 'RAFFLE_CONTROLS.CONFIRM.OPEN_CANCEL_TEXT',
      },
    }

    return dialogMap[raffleStatus]
  }
}
